import { useEffect, useState } from "react";
import { FaEdit, FaFemale, FaMale, FaPlusCircle, FaSync } from "react-icons/fa";
import DescriptionInput from "../DescriptionInput";
import LoadingSpinner from "../LoadingSpinner";
import MessageModel from "../MessageModel";
import {
  addOccupation,
  addPersonalInformation,
  drpBadPoints,
  drpGoodPoints,
  drpOccupations,
  drpSocialDifficulties,
  getPatientPersonalInfo,
  updatePersonalInformation,
} from "../../functions/patient";
import VoiceToText from "../VoiceToText";
import EditButton from "../EditButton";
import DialogModel from "../model/DialogModel";

const maritalStatusOptions = [
  { value: "never_married", text: "Single" },
  {
    value: "married_first_time",
    text: "Living Together",
  },
  {
    value: "married_second_time",
    text: "Married",
  },
  { value: "separated", text: "Separated" },
  { value: "divorced_not_remarried", text: "Divorced and Not Remarried" },
  { value: "widowed_not_remarried", text: "Widowed and Not Remarried" },
];



const printStyles = `
   @media print {
    @page { size: A4; margin: 1cm; }
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .print-break { page-break-before: always; }
  }
  @media screen {
    .print-break { 
      break-before: page;
      margin-top: 30mm;
    }
`;



function MaritalInfoPrint({ personalInformation, maritalStatusOptions }) {
  // ---------- helpers ----------
  const getMaritalText = () => {
    const opt = maritalStatusOptions.find(
      o => o.value === personalInformation.maritalStatus.value
    );
    return opt ? opt.text : "N/A";
  };

  const splitAges = (field) => {
    const raw = personalInformation[field].value;
    if (!raw) return [];
    return raw.split(",").map(a => a.trim()).filter(Boolean);
  };

  const maleAges   = splitAges("maleChildrenAges");
  const femaleAges = splitAges("femaleChildrenAges");

  const religiosity = personalInformation.religiosity.value
    ? Number(personalInformation.religiosity.value)
    : null;

  // ---------- render ----------
  return (
    <>
      {/* inject CSS */}
      <style>{printStyles}</style>

      {/* whole block – hidden on screen */}
      <div className="print-break mx-auto max-w-[210mm] bg-white font-sans text-sm leading-relaxed">

          <h1 className="text-xl text-center mb-5 font-bold text-sky-700">Personal</h1>

            <h3 className="mb-3 border-b-2 border-sky-700 pb-1 text-lg font-bold text-sky-700">
                 Marital Details
          </h3>


        {/* ---------- Two-column layout ---------- */}
        <section className="print-section grid grid-cols-2 md:grid-cols-2 gap-6">

          {/* ---- LEFT COLUMN ---- */}
          <div className="space-y-4">

            {/* Marital status */}
            <div className="print-field flex justify-between items-start py-2 border-b border-gray-300">
              <span className="font-medium text-gray-700 w-1/2">
                Present Marital Status
              </span>
              <span className="text-gray-900 w-1/2 text-right">
                {getMaritalText()}
              </span>
            </div>

            {/* Male children ages */}
            <div className="print-field flex justify-between items-start py-2 border-b border-gray-300">
              <span className="font-medium text-gray-700 w-1/2">
                Ages of Male Children
              </span>
              <div className="text-gray-900 w-1/2 text-right">
                {maleAges.length ? maleAges.join(", ") : <span className="italic text-gray-500">N/A</span>}
              </div>
            </div>

            {/* Female children ages */}
            <div className="print-field flex justify-between items-start py-2 border-b border-gray-300">
              <span className="font-medium text-gray-700 w-1/2">
                Ages of Female Children
              </span>
              <div className="text-gray-900 w-1/2 text-right">
                {femaleAges.length ? femaleAges.join(", ") : <span className="italic text-gray-500">N/A</span>}
              </div>
            </div>
          </div>

          {/* ---- RIGHT COLUMN ---- */}
          <div className="space-y-4">

            {/* Years married (only when applicable) */}
            {["married_first_time", "married_second_time"].includes(
              personalInformation.maritalStatus.value
            ) && (
              <div className="print-field flex justify-between items-center py-2 border-b border-gray-300">
                <span className="font-medium text-gray-700 w-2/3">
                  Number of Years Married to Present Spouse
                </span>
                <span className="text-gray-900 w-1/3 text-right">
                  {personalInformation.yearsMarried.value} yrs
                </span>
              </div>
            )}

            {/* Religiosity */}
            <div className="print-field py-2">
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium text-gray-700">
                  How religious are you?
                </span>
                <span className="text-gray-900 font-semibold">
                  {religiosity ? `${religiosity} / 9` : "N/A"}
                </span>
              </div>

              {religiosity ? (
                <div className="text-xs text-gray-600 flex justify-between">
                  <span>1 = Very Religious</span>
                  <span>5 = Average</span>
                  <span>9 = Atheist</span>
                </div>
              ) : (
                <span className="italic text-gray-500">Not specified</span>
              )}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}





function PersonalInsightsPrint({ personalInformation }) {
  /* ---------- helpers ---------- */
  const renderList = (field) => {
    const items = personalInformation[field].value || [];
    if (!Array.isArray(items) || items.length === 0)
      return <span className="italic text-gray-500">N/A</span>;

    return (
      <ul className="list-disc pl-5 space-y-1">
        {items.map((it, i) => (
          <li key={i} className="text-gray-700">{it}</li>
        ))}
      </ul>
    );
  };

  return (
    <>
      <style>{printStyles}</style>

      <div className="print-only mx-auto max-w-[210mm] bg-white font-sans text-sm leading-relaxed">
        <header className="print-header text-left mb-4">
        <h3 className="mb-3 border-b-2 border-sky-700 pb-1 text-lg font-bold text-sky-700">
          Personal Insights
        </h3>
</header>
        <section className="print-section space-y-5">

          {/* Things Liked */}
          {/* <div className="print-field">
            <span className="font-medium text-gray-700">Things you like to do most</span>
            <p className="mt-1 text-gray-900 whitespace-pre-line">
              {personalInformation.thingsLiked.value || (
                <span className="italic text-gray-500">N/A</span>
              )}
            </p>
          </div> */}

          {/* Assets / Good points */}
          {/* <div className="print-field">
            <span className="font-medium text-gray-700">Main assets and good points</span>
            <div className="mt-1">{renderList("assets")}</div>
          </div> */}

          {/* Bad points */}
          {/* <div className="print-field">
            <span className="font-medium text-gray-700">Main bad points</span>
            <div className="mt-1">{renderList("badPoints")}</div>
          </div> */}

          {/* Social difficulties */}
          <div className="print-field">
            <span className="font-medium text-gray-700">Main social difficulties</span>
            <div className="mt-1">{renderList("socialDifficulties")}</div>
          </div>

          {/* Love & Sex difficulties */}
          <div className="print-field">
            <span className="font-medium text-gray-700">Main love and sex difficulties</span>
            <p className="mt-1 text-gray-900 whitespace-pre-line">
              {personalInformation.loveSexDifficulties.value || (
                <span className="italic text-gray-500">N/A</span>
              )}
            </p>
          </div>

          {/* Work / School difficulties */}
          <div className="print-field">
            <span className="font-medium text-gray-700">Main school or work difficulties</span>
            <p className="mt-1 text-gray-900 whitespace-pre-line">
              {personalInformation.schoolWorkDifficulties.value || (
                <span className="italic text-gray-500">N/A</span>
              )}
            </p>
          </div>

          {/* Life goals */}
          {/* <div className="print-field">
            <span className="font-medium text-gray-700">Main life goals</span>
            <p className="mt-1 text-gray-900 whitespace-pre-line">
              {personalInformation.lifeGoals.value || (
                <span className="italic text-gray-500">N/A</span>
              )}
            </p>
          </div> */}

          {/* Things to change */}
          {/* <div className="print-field">
            <span className="font-medium text-gray-700">Things you would most like to change about yourself</span>
            <p className="mt-1 text-gray-900 whitespace-pre-line">
              {personalInformation.thingsToChange.value || (
                <span className="italic text-gray-500">N/A</span>
              )}
            </p>
          </div> */}

        </section>
      </div>
    </>
  );
}


function OccupationInfoPrint({ personalInformation }) {
  return (
    <>
      <style>{printStyles}</style>

      <div className="print-only mx-auto max-w-[210mm] bg-white font-sans text-sm leading-relaxed">

        <h3 className="mb-3 border-b-2 border-sky-700 pb-1 text-lg font-bold text-sky-700">
          Occupation Information
        </h3>

        <section className="print-section space-y-4">

<div className="grid grid-cols-2 gap-5">
          {/* Trained for */}
          <div className="print-field flex justify-between items-center py-2 border-b border-gray-300">
            <span className="font-medium text-gray-700 w-1/2">
              Occupation(s) mainly trained for
            </span>
            <span className="text-gray-900 w-1/2 text-right">
              {personalInformation.occupationTrained.value || (
                <span className="italic text-gray-500">N/A</span>
              )}
            </span>
          </div>

          {/* Present occupation */}
          <div className="print-field flex justify-between items-center py-2 border-b border-gray-300">
            <span className="font-medium text-gray-700 w-1/2">
              Present occupation
            </span>
            <span className="text-gray-900 w-1/2 text-right">
              {personalInformation.occupation.value || (
                <span className="italic text-gray-500">N/A</span>
              )}
            </span>
          </div>

          {/* Status */}
          <div className="print-field flex justify-between items-center py-2">
            <span className="font-medium text-gray-700 w-1/2">
              Occupation status
            </span>
            <span className="text-gray-900 w-1/2 text-right">
              {personalInformation.occupationFullTime.value || (
                <span className="italic text-gray-500">N/A</span>
              )}
            </span>
          </div>
          </div>

        </section>
      </div>
    </>
  );
}



const TabPersonalInformationIndividual = ({ id, refreshTabDetails, setActiveTab ,printPreviewMode}) => {

  const [personalInformationErrors, setPersonalInformationErrors] = useState(
    {}
  );
  const [mode, setMode] = useState("add");
  const [editingSection, setEditingSection] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [initialPersonalInformation, setInitialPersonalInformation] =
    useState(null); // Store initial state for cancel
  const [modal, setModal] = useState({
    isOpen: false,
    message: "",
    type: "error",
  });

  const [isOccupationLoading, setIsOccupationLoading] = useState(false);

    const [socialDifficultiesOptions, setSocialDifficultiesOptions] = useState([]);
  

  const [personalInformation, setPersonalInformation] = useState({
    maritalStatus: {
      label: "Present Marital Status",
      value: "",
      isTouched: false,
      isValid: false,
      required: false,
      dataType: "string",
    },
    yearsMarried: {
      label: "Number of Years Married to Present Spouse",
      value: "",
      isTouched: false,
      isValid: true,
      required: false,
      dataType: "number",
    },
    maleChildrenAges: {
      label: "Ages of Male Children",
      value: "",
      isTouched: false,
      isValid: true,
      required: false,
      dataType: "string",
    },
    femaleChildrenAges: {
      label: "Ages of Female Children",
      value: "",
      isTouched: false,
      isValid: true,
      required: false,
      dataType: "string",
    },
    religiosity: {
      label: "Religiosity",
      value: "",
      isTouched: false,
      isValid: true,
      required: false,
      dataType: "number",
    },
    thingsLiked: {
      label: "Things You Like to Do Most",
      value: "",
      isTouched: false,
      isValid: true,
      required: false,
      dataType: "string",
    },
    assets: {
      label: "Main Assets and Good Points",
      value: "",
      isTouched: false,
      isValid: true,
      required: false,
      dataType: "array",
    },
    badPoints: {
      label: "Main Bad Points",
      value: "",
      isTouched: false,
      isValid: true,
      required: false,
      dataType: "array",
    },
    socialDifficulties: {
      label: "Main Social Difficulties",
      value: "",
      isTouched: false,
      isValid: true,
      required: false,
      dataType: "string",
    },
    loveSexDifficulties: {
      label: "Main Love and Sex Difficulties",
      value: "",
      isTouched: false,
      isValid: true,
      required: false,
      dataType: "string",
    },
    schoolWorkDifficulties: {
      label: "Main School or Work Difficulties",
      value: "",
      isTouched: false,
      isValid: true,
      required: false,
      dataType: "string",
    },
    lifeGoals: {
      label: "Main Life Goals",
      value: "",
      isTouched: false,
      isValid: true,
      required: false,
      dataType: "string",
    },
    thingsToChange: {
      label: "Things to Change About Yourself",
      value: "",
      isTouched: false,
      isValid: true,
      required: false,
      dataType: "string",
    },
    occupationTrained: {
      label: "Occupation(s) Trained For",
      value: "",
      isTouched: false,
      isValid: false,
      required: false,
      dataType: "string",
    },
    occupation: {
      label: "Present Occupation",
      value: "",
      isTouched: false,
      isValid: false,
      required: false,
      dataType: "string",
    },
    occupationFullTime: {
      label: "Occupation Status",
      value: "",
      isTouched: false,
      isValid: false,
      required: false,
      dataType: "string",
    },
  });

  const loadPersonalInformationData = async () => {
    setIsLoading(true);
    //const result =await getPersonalInformationData(id);
    const result = await getPatientPersonalInfo(id);
    const patientData = result.data;

    if (patientData.error) {
      console.log("patientData.error", patientData.error);
      setModal({
        isOpen: true,
        message: patientData.error.message,
        type: "error",
      });
    }

    console.log("loadPersonalInformationData:", result, id);

    if (patientData) {
      setMode("edit");
    }

    if (patientData) {
      setPersonalInformation({
        maritalStatus: {
          ...personalInformation.maritalStatus,
          value: patientData.maritalStatus,
          isTouched: false,
          isValid: true,
        },
        yearsMarried: {
          ...personalInformation.yearsMarried,
          value: patientData.yearsMarried,
          isTouched: false,
          isValid: true,
          required:
            patientData.maritalStatus === "married_first_time" ||
            patientData.maritalStatus === "married_second_time",
        },
        maleChildrenAges: {
          ...personalInformation.maleChildrenAges,
          value: patientData.maleChildrenAges,
          isTouched: false,
          isValid: true,
        },
        femaleChildrenAges: {
          ...personalInformation.femaleChildrenAges,
          value: patientData.femaleChildrenAges,
          isTouched: false,
          isValid: true,
        },
        religiosity: {
          ...personalInformation.religiosity,
          value: patientData.religiosity,
          isTouched: false,
          isValid: true,
        },
        thingsLiked: {
          ...personalInformation.thingsLiked,
          value: patientData.thingsLiked,
          isTouched: false,
          isValid: true,
        },
        assets: {
          ...personalInformation.assets,
          value:patientData.assets?patientData.assets.split(";;"):'',
          isTouched: false,
          isValid: true,
        },
        badPoints: {
          ...personalInformation.badPoints,
          value:  patientData.badPoints ? patientData.badPoints.split(";;"):'',
          isTouched: false,
          isValid: true,
        },
       socialDifficulties: {
          ...personalInformation.socialDifficulties,
          value:patientData.socialDifficulties ? patientData.socialDifficulties.split(";;"):'',
          isTouched: false,
          isValid: true,
        },
        loveSexDifficulties: {
          ...personalInformation.loveSexDifficulties,
          value: patientData.loveSexDifficulties,
          isTouched: false,
          isValid: true,
        },
        schoolWorkDifficulties: {
          ...personalInformation.schoolWorkDifficulties,
          value: patientData.schoolWorkDifficulties,
          isTouched: false,
          isValid: true,
        },
        lifeGoals: {
          ...personalInformation.lifeGoals,
          value: patientData.lifeGoals,
          isTouched: false,
          isValid: true,
        },
        thingsToChange: {
          ...personalInformation.thingsToChange,
          value: patientData.thingsToChange,
          isTouched: false,
          isValid: true,
        },
        occupationTrained: {
          ...personalInformation.occupationTrained,
          value: patientData.occupationTrained,
          isTouched: false,
          isValid: true,
        },
        occupation: {
          ...personalInformation.occupation,
          value: patientData.occupation,
          isTouched: false,
          isValid: true,
        },
        occupationFullTime: {
          ...personalInformation.occupationFullTime,
          value: patientData.occupationFullTime,
          isTouched: false,
          isValid: true,
        },
      });

      setIsLoading(false);
    } else {
      // setMode("add");
      setIsLoading(false);
    }
  };
  // Load mock data based on id
  useEffect(() => {
    if (id) {
      loadPersonalInformationData();
    }
  }, [id]);

  const [goodPointsOptions, setGoodPointsOptions] = useState([]);
  const [badPointsOptions, setBadPointsOptions] = useState([]);
  const [occupations, setOccupations] = useState([]);

  const loadDrpGoodPoints = async () => {
    const goodPoints = await drpGoodPoints();
    setGoodPointsOptions(goodPoints.data.results[0]);
  };

  const loadDrpBadPoints = async () => {
    const badPoints = await drpBadPoints();
    setBadPointsOptions(badPoints.data.results[0]);
  };

  const loadDrpOccupations = async () => {
    const occupations = await drpOccupations();
    setOccupations(occupations.data.results[0]);
  };
   const loadDrpSocialDifficulties = async () => {
      const result = await drpSocialDifficulties();
      setSocialDifficultiesOptions(result.data.results[0]);
    };

  useEffect(() => {
    loadDropdowns();
  }, []);

  const loadDropdowns = async () => {
    await loadDrpGoodPoints();
    await loadDrpBadPoints();
    await loadDrpOccupations();
        await loadDrpSocialDifficulties();
  };

  // Store initial state when entering edit mode
  useEffect(() => {
    setInitialPersonalInformation({ ...personalInformation });
  }, [editingSection]);

  // Toggle edit mode for a specific section
  const toggleSectionEdit = async (section) => {
    if (editingSection === section) {
      const isValid = await handleSubmitp(section);
      console.log("isValidoo.", isValid);
      if (isValid) setEditingSection(null);
    } else {
      setEditingSection(section);
    }
  };

  const handleSubmitp = async (section) => {
    setIsSaving(true);
    const isValid = validatePersonalInformation();
    if (!isValid) {
      console.log("Validation failed, not saving.");
      setIsSaving(false);
      return false;
    }

    console.log("section", section);
    const payload = {
      patientId: id,
      maritalStatus: personalInformation.maritalStatus.value,
      yearsMarried:
        personalInformation.yearsMarried.value === ""
          ? null
          : personalInformation.yearsMarried.value,
      maleChildrenAges: personalInformation.maleChildrenAges.value,
      femaleChildrenAges: personalInformation.femaleChildrenAges.value,
    religiosity: personalInformation.religiosity.value===""?null:personalInformation.religiosity.value,
      thingsLiked: personalInformation.thingsLiked.value,
      assets:personalInformation.assets.value ? personalInformation.assets.value.map((item) => ({ name: item })):null,
      badPoints: personalInformation.badPoints.value ? personalInformation.badPoints.value.map((item) => ({
        name: item,
      })):null,
 socialDifficulties:personalInformation.socialDifficulties.value ? personalInformation.socialDifficulties.value.map((item) => ({ name: item })):null,
      loveSexDifficulties: personalInformation.loveSexDifficulties.value,
      schoolWorkDifficulties: personalInformation.schoolWorkDifficulties.value,
      lifeGoals: personalInformation.lifeGoals.value,
      thingsToChange: personalInformation.thingsToChange.value,
      occupationTrained: personalInformation.occupationTrained.value===""?null:personalInformation.occupationTrained.value,
      occupation: personalInformation.occupation.value===""?null:personalInformation.occupation.value,
      occupationFullTime: personalInformation.occupationFullTime.value,
      pageName: "PatientBackgroundForm",
      isConfirm: true,
    };

    console.log("Save Payload:", payload);

    try {
      const res = await updatePersonalInformation(id, payload);
      console.log("update result:", res);

      if (res.data.error) {
        setModal({
          isOpen: true,
          message: res.data.error.message,
          type: "error",
        });
        setIsSaving(false);
        return;
      }

      if (res.data.outputValues.responseStatus === "failed") {
        setModal({
          isOpen: true,
          message: res.data.outputValues.outputMessage,
          type: "warning",
        });
        setIsSaving(false);
        return;
      }

      // if(section==="occupation"){
      //   console.log('dddhhhhhhlllllll drop')
      //     await loadDrpOccupations();
      // }

      await loadPersonalInformationData();

      setModal({
        isOpen: true,
        message: res.data.outputValues.outputMessage,
        type: "success",
      });

      setIsSaving(false);
      return true;
    } catch (err) {
      console.log("Save Payload: err", err.message);
      setModal({
        isOpen: true,
        message: err.message,
        type: "error",
      });
      //setPersonalInformation(initialPersonalInformation);
      setIsSaving(false);
      return false;
    }
  };


//occupation add panel
const [showOccuptionAddPanel,setShowOccuptionAddPanel]=useState(false);
const [newOccupationName,setNewOccupationName]=useState('');


const NewOccupationPanel = () => {
  return (
    <div className="mt-4 p-4 border border-blue-200 rounded-lg">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Enter New Occupation Name
      </label>
      <div className="flex gap-2">
        <input
          type="text"
          value={newOccupationName}
          onChange={(e) => setNewOccupationName(e.target.value)}
          placeholder="e.g., Data Scientist"
          className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
          autoFocus
        />
        <button
          onClick={async () => {
            if (!newOccupationName.trim()) {
              setModal({
                isOpen: true,
                message: "Please enter an occupation name.",
                type: "error",
              });
              return;
            }

            setIsSaving(true);
            const occupationRes = await addOccupation({ occupationName: newOccupationName.trim() });

            console.log('occupationRes',occupationRes)
            if (occupationRes.data.error) {
              setModal({
                isOpen: true,
                message: occupationRes.data.error.message,
                type: "error",
              });
              setIsSaving(false);
              return;
            }

            if (occupationRes.data.outputValues.responseStatus === "failed") {
              setModal({
                isOpen: true,
                message: occupationRes.data.outputValues.outputMessage,
                type: "warning",
              });
              setIsSaving(false);
              return;
            }

            await loadDrpOccupations();

            const occupatoinId=occupationRes.data.outputValues.occupationId;

            setPersonalInformation((prev) => ({
              ...prev,
              [selectedOccupationField]: {
                ...prev[selectedOccupationField],
                value: newOccupationName.trim(),
              },
              // occupation: {
              //   ...prev.occupation,
              //   value: newOccupationName.trim(),
              // },
            }));

            setNewOccupationName("");
            setShowOccuptionAddPanel(false);
            setIsSaving(false);

            setModal({
              isOpen: true,
              message: "New occupation added and selected!",
              type: "success",
            });
          }}
          disabled={isSaving}
          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
        >
          {isSaving ? "Adding..." : "Add"}
        </button>
        <button
          onClick={() => {
            setShowOccuptionAddPanel(false);
            setNewOccupationName("");
          }}
          className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

  // Validate individual field
  const validateField = (name, value, required, dataType) => {
   
    if (dataType === "string") {
    if (required && (typeof value !== "string" ? String(value).trim() : value.trim()) === "") {
  return `${name} is required`;
}
    }

    if (dataType === "array") {
      if (required && value.length === 0) {
        return `${name} is required`;
      }
    }

    if (value && dataType === "number") {
      if (isNaN(value) || Number(value) < 0) {
        return `${name} must be a valid non-negative number`;
      }
    }

    return "";
  };

  // Handle input changes
  const handleChangePersonalInfo = (e) => {
    const { name, value } = e.target;

if(value===0) // adding custom occupation
{
  setShowOccuptionAddPanel(true);

}


    const { required, dataType } = personalInformation[name];
    const error = validateField(
      personalInformation[name].label,
      value,
      required,
      dataType
    );

    setPersonalInformation((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value,
        isTouched: true,
        isValid: error === "",
      },
    }));

    setPersonalInformationErrors((prev) => ({
      ...prev,
      [name]: error,
    }));

    // Update yearsMarried requirement based on maritalStatus
    if (name === "maritalStatus") {
      const isMarriedNow =
        value === "married_first_time" || value === "married_second_time";
      setPersonalInformation((prev) => ({
        ...prev,
        yearsMarried: {
          ...prev.yearsMarried,
          required: isMarriedNow,
          isValid:
            !isMarriedNow ||
            (typeof prev.yearsMarried.value === "string"
              ? prev.yearsMarried.value.trim() !== ""
              : prev.yearsMarried.value !== null &&
                prev.yearsMarried.value !== undefined),
        },
      }));

      setPersonalInformationErrors((prev) => ({
        ...prev,
        yearsMarried:
          isMarriedNow && !prev.yearsMarried?.value
            ? "Number of Years Married is required for current marriage"
            : "",
      }));
    }
  };

  const handleSubjectChange = async (selectedOption, field) => {
    const { name, value } = selectedOption;

    console.log("handleSubjectChange name:", selectedOption.name);
    console.log("handleSubjectChange field", field);
    let outputSubjectId = "";

    const { required, dataType } = personalInformation[field];
    const error = validateField(
      personalInformation[field].label,
      selectedOption.name,
      required,
      dataType
    );

    setPersonalInformation((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        value: selectedOption.name,
        isTouched: true,
        isValid: error === "",
      },
    }));

    setPersonalInformationErrors((prev) => ({
      ...prev,
      [field]: error,
    }));
  };

  // Handle DescriptionInput changes
  const handleDescriptionChange = (fieldName, value) => {
    const { required, dataType } = personalInformation[fieldName];
    const error = validateField(
      personalInformation[fieldName].label,
      value,
      required,
      dataType
    );

    setPersonalInformation((prev) => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        value: value.split(";;").filter((item) => item.trim()),
        isTouched: true,
        isValid: error === "",
      },
    }));

    setPersonalInformationErrors((prev) => ({
      ...prev,
      [fieldName]: error,
    }));
  };

  // Handle occupation status change
  const handleOccupationStatusChange = (e) => {
    const value = e.target.value;
    setPersonalInformation((prev) => ({
      ...prev,
      occupationFullTime: {
        ...prev.occupationFullTime,
        value,
        isTouched: true,
        isValid: true,
      },
    }));
    setPersonalInformationErrors((prev) => ({
      ...prev,
      occupationFullTime: "",
    }));
  };

  // Validate entire form
  const validatePersonalInformation = () => {
    const errors = {};
    let isFormValid = true;
    const updatedInfo = { ...personalInformation };

    Object.entries(personalInformation).forEach(([key, field]) => {
      if (!field || typeof field !== "object" || !("value" in field)) return;

      const { value, required, dataType } = field;

      console.log("key,value ", key, value);
      let errorMessage = validateField(field.label, value, required, dataType);

      if (
        key === "yearsMarried" &&
        ["married_first_time", "married_second_time"].includes(
          personalInformation.maritalStatus.value
        )
      ) {
        if (
          value === null ||
          value === undefined ||
          (typeof value === "string" && value.trim() === "")
        ) {
          errorMessage =
            "Number of Years Married is required for current marriage";
        }
      }

      if (errorMessage) {
        isFormValid = false;
        errors[key] = errorMessage;
        updatedInfo[key].isValid = false;
      } else {
        updatedInfo[key].isValid = true;
      }

      //updatedInfo[key].isTouched = true;
      updatedInfo[key].isTouched = updatedInfo[key].isTouched || false;
    });

    setPersonalInformation(updatedInfo);
    setPersonalInformationErrors(errors);
    return isFormValid;
  };

  // Generate payload for submission
  const generateSubmitPayload = (infoObject) => {
    const payload = {};
    for (const key in infoObject) {
      if (infoObject.hasOwnProperty(key)) {
        payload[key] = infoObject[key].value;
      }
    }
    return payload;
  };

  // Handle form submission
  const handleSubmitPersonalInformation = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    const isValid = validatePersonalInformation();
    console.log("isValid", isValid);
    if (isValid) {
      const payload = {
        patientId: id,
        maritalStatus: personalInformation.maritalStatus.value,
        yearsMarried:
          personalInformation.yearsMarried.value === ""
            ? null
            : personalInformation.yearsMarried.value,
        maleChildrenAges: personalInformation.maleChildrenAges.value,
        femaleChildrenAges: personalInformation.femaleChildrenAges.value,
        religiosity: personalInformation.religiosity.value===""?null:personalInformation.religiosity.value,
        thingsLiked: personalInformation.thingsLiked.value,
         assets:personalInformation.assets.value ? personalInformation.assets.value.map((item) => ({ name: item })):null,
        badPoints:personalInformation.assets.value ? personalInformation.badPoints.value.map((item) => ({ name: item })):null,
         socialDifficulties:personalInformation.socialDifficulties.value? personalInformation.socialDifficulties.value.map((item) => ({ name: item })):null,
        loveSexDifficulties: personalInformation.loveSexDifficulties.value,
        schoolWorkDifficulties:
          personalInformation.schoolWorkDifficulties.value,
        lifeGoals: personalInformation.lifeGoals.value,
        thingsToChange: personalInformation.thingsToChange.value,
              occupationTrained: personalInformation.occupationTrained.value===""?null:personalInformation.occupationTrained.value,
      occupation: personalInformation.occupation.value===""?null:personalInformation.occupation.value,
        occupationFullTime: personalInformation.occupationFullTime.value,
        pageName: "PatientBackgroundForm",
        isConfirm: true,
      };

      const submitPayloadWithPatientId = {
        ...payload,
        patientId: id,
      };

      try {
        const res = await addPersonalInformation(submitPayloadWithPatientId);
        console.log("Save res:", res);
        //await loadPersonalInformationData();
        console.log("update result:", res);

        if (res.data.error) {
          setModal({
            isOpen: true,
            message: res.data.error.message,
            type: "error",
          });
          setIsSaving(false);
          return;
        }

        if (res.data.outputValues.responseStatus === "failed") {
          setModal({
            isOpen: true,
            message: res.data.outputValues.outputMessage,
            type: "warning",
          });
          setIsSaving(false);
          return;
        }

        // if(section==="occupation"){
        //   console.log('dddhhhhhhlllllll drop')
        //     await loadDrpOccupations();
        // }

        await loadPersonalInformationData();

        setModal({
          isOpen: true,
          message: res.data.outputValues.outputMessage,
          type: "success",
        });

        refreshTabDetails(Math.random());
      } catch (err) {
        console.log("Save Payload: err", err.message);
        setModal({
          isOpen: true,
          message: err.message,
          type: "error",
        });
        setIsSaving(false);
      }
    }

    setIsSaving(false);
  };

  const [selectedOccupationField,setSelectedOccupationField]=useState('');
  // Function to handle cancel action
  const handleCancel = (editingSection) => {
    if (initialPersonalInformation) {
      setPersonalInformation(initialPersonalInformation);
      setPersonalInformationErrors({});
    }

    setEditingSection(null); // Exit edit mode
  };

  // Helper function to render list items
  const renderListItems = (value) => {
    let items = [];
    if (typeof value === "string") {
      items = value.split(";;").filter((item) => item.trim());
    } else if (Array.isArray(value)) {
      items = value.filter((item) => item.trim());
    }

    if (items.length === 0) {
      return <p className="text-gray-500 italic">N/A</p>;
    }

    return (
      <ul className="list-disc pl-5 space-y-1">
        {items.map((item, index) => (
          <li key={index} className="text-gray-700">
            {item}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <>
      <MessageModel
        isOpen={modal.isOpen}
        onClose={() => setModal({ isOpen: false, message: "", type: "error" })}
        message={modal.message}
        type={modal.type}
      />


      <DialogModel
        header={"Add New Occupation"}
        visible={showOccuptionAddPanel}
        onHide={() => setShowOccuptionAddPanel(false)}
      >


<NewOccupationPanel />

</DialogModel>

      {!isLoading ? (
        <div className="px-8">
          {/* Personal Details */}
       {!printPreviewMode ?
          <section className="mb-12">



            <div className="flex justify-between items-center mb-2  pb-2">
              <h3 className="text-xl font-semibold text-gray-700">
                  Marital Details
              </h3>
              { mode !== "add" && (
                <div className="flex space-x-4">
                  <EditButton
                    onClick={() => toggleSectionEdit("personalDetails")}
                    ariaLabel={
                      editingSection === "personalDetails"
                        ? "Save Personal Details"
                        : "Edit Personal Details"
                    }
                    disabled={isSaving}
                  >
                    {editingSection === "personalDetails"
                      ? isSaving
                        ? "Saving..."
                        : "Save"
                      : "Edit"}
                  </EditButton>

                  {/* <button
                    onClick={() => toggleSectionEdit("personalDetails")}
                    className="flex items-center bg-sky-600 text-white px-4 py-2 rounded-lg hover:bg-sky-700 transition-all duration-200"
                    aria-label={
                      editingSection === "personalDetails"
                        ? "Save Personal Details"
                        : "Edit Personal Details"
                    }
                    disabled={isSaving}
                  >
                    <FaEdit className="mr-2" />
                    {editingSection === "personalDetails"
                      ? isSaving
                        ? "Saving..."
                        : "Save"
                      : "Edit"}
                  </button> */}
                  {editingSection === "personalDetails" && (
                    <button
                      onClick={() => handleCancel("personalDetails")}
                      className="flex items-center bg-gray-300 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-400 transition-all duration-200"
                      aria-label="Cancel Editing"
                      disabled={isSaving}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              )}
            </div>
            {editingSection === "personalDetails" || mode === "add" ? (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Present Marital Status{" "}
                    {personalInformation.maritalStatus.required && (
                      <span className="text-red-500">*</span>
                    )}
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                    {maritalStatusOptions.map((option) => (
                      <label key={option.value} className="flex items-center">
                        <input
                          type="radio"
                          name="maritalStatus"
                          value={option.value}
                          checked={
                            personalInformation.maritalStatus.value ===
                            option.value
                          }
                          onChange={handleChangePersonalInfo}
                          className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300"
                          aria-label={option.text}
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          {option.text}
                        </span>
                      </label>
                    ))}
                  </div>
                  {personalInformationErrors.maritalStatus && (
                    <p className="mt-1 text-sm text-red-600">
                      {personalInformationErrors.maritalStatus}
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Number of Years Married to Present Spouse
                      {personalInformation.yearsMarried.required && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <input
                      type="number"
                      name="yearsMarried"
                      value={personalInformation.yearsMarried.value}
                      onChange={handleChangePersonalInfo}
                      className="mt-1 w-full p-3 border text-sm border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-200"
                      placeholder="eg.,1, 1.5, 2"
                      disabled={
                        !["married_first_time", "married_second_time"].includes(
                          personalInformation.maritalStatus.value
                        )
                      }
                      aria-label="Years married"
                    />
                    {personalInformationErrors.yearsMarried && (
                      <p className="mt-1 text-sm text-red-600">
                        {personalInformationErrors.yearsMarried}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Ages of Male Children
                      {personalInformation.maleChildrenAges.required && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <input
                      name="maleChildrenAges"
                      type="text"
                      value={personalInformation.maleChildrenAges.value}
                      onChange={handleChangePersonalInfo}
                      className="mt-1 w-full p-3 border text-sm border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-200"
                      placeholder="e.g., 5, 10, 15"
                      aria-label="Male children ages"
                    />
                    {personalInformationErrors.maleChildrenAges && (
                      <p className="mt-1 text-sm text-red-600">
                        {personalInformationErrors.maleChildrenAges}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Ages of Female Children{" "}
                      {personalInformation.femaleChildrenAges.required && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <input
                      name="femaleChildrenAges"
                      type="text"
                      value={personalInformation.femaleChildrenAges.value}
                      onChange={handleChangePersonalInfo}
                      className="mt-1 w-full p-3 border text-sm border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-200"
                      placeholder="e.g., 3, 8"
                      aria-label="Female children ages"
                    />
                    {personalInformationErrors.femaleChildrenAges && (
                      <p className="mt-1 text-sm text-red-600">
                        {personalInformationErrors.femaleChildrenAges}
                      </p>
                    )}
                  </div>
                </div>
              
  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="col-span-2 mx-4">
                <label className="block text-sm font-medium text-gray-700">
                  How religious are you?{personalInformation.religiosity.required && <span className="text-red-500">*</span>} (1 = Very Religious, 5 = Average, 9 = Atheist)
                </label>
                <div className="mt-2 flex items-start justify-between">
                  <div className="flex w-full justify-between">
                    <span className="text-xs text-gray-600 -ml-1">Very</span>
                    <span className="text-xs text-gray-600">Average</span>
                    <span className="text-xs text-gray-600 -mr-1">Atheist</span>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-1 space-x-1">
                  {[...Array(9)].map((_, i) => {
                    const value = (i + 1).toString();
                    return (
                      <label key={value} className="flex flex-col items-center">
                        <input
                          type="radio"
                          name="religiosity"
                          value={value}
                          checked={personalInformation.religiosity.value == value}
                          onChange={handleChangePersonalInfo}
                          className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300 custom-radio"
                          aria-label={`Religiosity level ${value}`}
                        />
                        <span className="mt-1 text-sm text-gray-700">{value}</span>
                      </label>
                    );
                  })}
                </div>
                {personalInformationErrors.religiosity && (
                  <p className="mt-1 text-sm text-red-600">{personalInformationErrors.religiosity}</p>
                )}
              </div>
              <div></div>
            </div>


              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white border-gray-200 rounded-lg p-4 border-2">
                <div className="mr-5">
                  <div className="flex justify-between items-center py-2.5 border-b border-gray-200 last:border-b-0">
                    <span className="font-bold text-sm w-1/3">
                      Present Marital Status
                    </span>
                    <span className="text-gray-700 text-sm w-2/3 text-right">
                      {
                        maritalStatusOptions.find(
                          (opt) =>
                            opt.value ===
                            personalInformation.maritalStatus.value
                        )?.text
                      }
                    </span>
                  </div>

                  <div className="flex justify-between items-center py-2.5 border-b border-gray-200 last:border-b-0">
                    <span className="font-bold text-sm w-1/3">
                      Ages of Male Children
                    </span>
                    <span className="text-gray-700 text-sm w-2/3 text-right flex flex-wrap justify-end gap-2">
                      {personalInformation.maleChildrenAges.value
                        ? personalInformation.maleChildrenAges.value
                            .split(",")
                            .map((age, index) => (
                              <span
                                key={`male-${index}`}
                                className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full"
                              >
                                <FaMale className="mr-1" /> {age.trim()}
                              </span>
                            ))
                        : <p className="text-gray-500 italic">N/A</p>}
                    </span>
                  </div>

                  <div className="flex justify-between items-center py-2.5 border-b border-gray-200 last:border-b-0">
                    <span className="font-bold text-sm w-1/3">
                      Ages of Female Children
                    </span>
                    <span className="text-gray-700 text-sm w-2/3 text-right flex flex-wrap justify-end gap-2">
                      {personalInformation.femaleChildrenAges.value
                        ? personalInformation.femaleChildrenAges.value
                            .split(",")
                            .map((age, index) => (
                              <span
                                key={`female-${index}`}
                                className="inline-flex items-center px-2 py-1 bg-pink-100 text-pink-800 text-xs font-medium rounded-full"
                              >
                                <FaFemale className="mr-1" /> {age.trim()}
                              </span>
                            ))
                        :  <p className="text-gray-500 italic">N/A</p>}
                    </span>
                  </div>
                </div>
                <div className="ml-5">
                  {["married_first_time", "married_second_time"].includes(
                    personalInformation.maritalStatus.value
                  ) ? (
                    <div className="flex justify-between items-center py-2.5 border-b border-gray-200 last:border-b-0">
                      <span className="font-bold text-sm w-2/3">
                        Number of Years Married to Present Spouse
                      </span>
                      <span className="text-gray-700 text-sm w-1/3 text-right">
                        {personalInformation.yearsMarried.value} yrs
                      </span>
                    </div>
                  ) : null}
                  <div className="py-2.5">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-bold text-sm text-gray-800">
                        How religious are you?
                      </span>
                      <span className="text-gray-600 text-sm font-medium">
                        {personalInformation.religiosity.value}
                      </span>
                    </div>
                    <div className="relative w-full h-3 bg-gray-100 rounded-full overflow-hidden shadow-sm transition-all duration-300 hover:shadow-md">
                      <div
                        className={`absolute h-full ${
                          personalInformation.religiosity.value
                            ? "bg-green-500"
                            : "bg-gray-200"
                        } transition-all duration-500 ease-out`}
                        style={{
                          width: `${
                            personalInformation.religiosity.value
                              ? ((personalInformation.religiosity.value - 1) /
                                  8) *
                                100
                              : 0
                          }%`,
                        }}
                      ></div>
                      {personalInformation.religiosity.value && (
                        <div
                          className="absolute top-0 h-full w-1 bg-gray-800 shadow-lg"
                          style={{
                            left: `calc(${
                              ((personalInformation.religiosity.value - 1) /
                                8) *
                              100
                            }% - 2px)`,
                          }}
                        ></div>
                      )}
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-2 font-medium">
                      <span className="hover:text-green-600 transition-colors">
                        Very Religious (1)
                      </span>
                      <span className="hover:text-gray-600 transition-colors">
                        Average (5)
                      </span>
                      <span className="hover:text-green-600 transition-colors">
                        Atheist (9)
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}


          </section>

:
          <section className="mb-12">
          
<MaritalInfoPrint personalInformation={personalInformation} maritalStatusOptions={maritalStatusOptions} />
</section>
          }


          {/* Personal Insights */}
                 {!printPreviewMode ?
          <section className="mb-12">
            <div className="flex justify-between items-center mb-2 pb-2">
              <h3 className="text-xl font-semibold text-gray-700">
                Personal Insights
              </h3>
              {mode !== "add" && (
                <div className="flex space-x-4">
                  <EditButton
                    onClick={() => toggleSectionEdit("insights")}
                    ariaLabel={
                      editingSection === "insights"
                        ? "Save Personal Insights"
                        : "Edit Personal Insights"
                    }
                    disabled={isSaving}
                  >
                    {editingSection === "insights"
                      ? isSaving
                        ? "Saving..."
                        : "Save"
                      : "Edit"}
                  </EditButton>

                  {/* <button
                    onClick={() => toggleSectionEdit("insights")}
                    className="flex items-center bg-sky-600 text-white px-4 py-2 rounded-lg hover:bg-sky-700 transition-all duration-200"
                    aria-label={
                      editingSection === "insights"
                        ? "Save Personal Insights"
                        : "Edit Personal Insights"
                    }
                    disabled={isSaving}
                  >
                    <FaEdit className="mr-2" />
                    {editingSection === "insights"
                      ? isSaving
                        ? "Saving..."
                        : "Save"
                      : "Edit"}
                  </button> */}
                  {editingSection === "insights" && (
                    <button
                      onClick={() => handleCancel("insights")}
                      className="flex items-center bg-gray-300 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-400 transition-all duration-200"
                      aria-label="Cancel Editing"
                      disabled={isSaving}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              )}
            </div>
            {editingSection === "insights" || mode === "add" ? (
              <div className="space-y-6">
                {/* <div>
                  <label className="block text-sm font-medium text-gray-700">
                    List the things you like to do most, kinds of things and
                    persons that give you pleasure{" "}
                    {personalInformation.thingsLiked.required && (
                      <span className="text-red-500">*</span>
                    )}
                  </label>
                  <VoiceToText
                    name="thingsLiked"
                    value={personalInformation.thingsLiked.value}
                    onChange={handleChangePersonalInfo}
                    className="mt-1 w-full p-3 border text-sm border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-200"
                    rows="4"
                    placeholder="Describe your interests and pleasures"
                    aria-label="Things liked"
                  />
                  {personalInformationErrors.thingsLiked && (
                    <p className="mt-1 text-sm text-red-600">
                      {personalInformationErrors.thingsLiked}
                    </p>
                  )}
                </div> */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* <div>
                    <DescriptionInput
                      patient={personalInformation}
                      setValue={(value) => {
                        handleDescriptionChange("assets", value);
                      }}
                      setPatient={(newPatient) => {
                        setPersonalInformation(newPatient);
                      }}
                      isEditing={
                        editingSection === "insights" || mode === "add"
                      }
                      fieldName="assets"
                      label="List your main assets and good points"
                      placeholder="Select or enter your strengths"
                      descriptionOptions={goodPointsOptions}
                      isTypeable={false}
                    />
                    {personalInformationErrors.assets && (
                      <p className="mt-1 text-sm text-red-600">
                        {personalInformationErrors.assets}
                      </p>
                    )}
                  </div> */}
                  {/* <div>
                    <DescriptionInput
                      patient={personalInformation}
                      setValue={(value) => {
                        handleDescriptionChange("badPoints", value);
                      }}
                      setPatient={(newPatient) => {
                        setPersonalInformation(newPatient);
                      }}
                      isEditing={
                        editingSection === "insights" || mode === "add"
                      }
                      fieldName="badPoints"
                      label="List your main bad points"
                      placeholder="Select or enter your weaknesses"
                      descriptionOptions={badPointsOptions}
                      isTypeable={false}
                    />
                    {personalInformationErrors.badPoints && (
                      <p className="mt-1 text-sm text-red-600">
                        {personalInformationErrors.badPoints}
                      </p>
                    )}
                  </div> */}
                </div>
                    <div>
                
                   <DescriptionInput
                    patient={personalInformation}
                    setValue={(value) => {
                      handleDescriptionChange("socialDifficulties", value);
                    }}
                    setPatient={(newPatient) => {
                      setPersonalInformation(newPatient);
                    }}
                    isEditing={editingSection === "insights" || mode === "add"}
                    fieldName="socialDifficulties"
                    label="List your main social difficulties"
                    placeholder="Select social challenges"
                    descriptionOptions={socialDifficultiesOptions}
                    isTypeable={false}
                  />
                  {personalInformationErrors.socialDifficulties && (
                    <p className="mt-1 text-sm text-red-600">
                      {personalInformationErrors.socialDifficulties}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    List your main love and sex difficulties
                    {personalInformation.loveSexDifficulties.required && (
                      <span className="text-red-500">*</span>
                    )}
                  </label>
                  <VoiceToText
                    name="loveSexDifficulties"
                    value={personalInformation.loveSexDifficulties.value}
                    onChange={handleChangePersonalInfo}
                    className="mt-1 w-full p-3 border text-sm border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-200"
                    rows="4"
                    placeholder="Describe relationship challenges"
                    aria-label="Love and sex difficulties"
                  />
                  {personalInformationErrors.loveSexDifficulties && (
                    <p className="mt-1 text-sm text-red-600">
                      {personalInformationErrors.loveSexDifficulties}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    List your main work difficulties
                    {personalInformation.schoolWorkDifficulties.required && (
                      <span className="text-red-500">*</span>
                    )}
                  </label>
                  <VoiceToText
                    name="schoolWorkDifficulties"
                    value={personalInformation.schoolWorkDifficulties.value}
                    onChange={handleChangePersonalInfo}
                    className="mt-1 w-full p-3 border text-sm border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-200"
                    rows="4"
                    placeholder="Describe professional/academic challenges"
                    aria-label="School or work difficulties"
                  />
                  {personalInformationErrors.schoolWorkDifficulties && (
                    <p className="mt-1 text-sm text-red-600">
                      {personalInformationErrors.schoolWorkDifficulties}
                    </p>
                  )}
                </div>
                {/* <div>
                  <label className="block text-sm font-medium text-gray-700">
                    List your main life goals
                    {personalInformation.lifeGoals.required && (
                      <span className="text-red-500">*</span>
                    )}
                  </label>
                  <VoiceToText
                    name="lifeGoals"
                    value={personalInformation.lifeGoals.value}
                    onChange={handleChangePersonalInfo}
                    className="mt-1 w-full p-3 border text-sm border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-200"
                    rows="4"
                    placeholder="List your life goals"
                    aria-label="Life goals"
                  />
                  {personalInformationErrors.lifeGoals && (
                    <p className="mt-1 text-sm text-red-600">
                      {personalInformationErrors.lifeGoals}
                    </p>
                  )}
                </div> */}
                {/* <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Things Most Like to Change
                    {personalInformation.thingsToChange.required && (
                      <span className="text-red-500">*</span>
                    )}
                  </label>
                  <VoiceToText
                    name="thingsToChange"
                    value={personalInformation.thingsToChange.value}
                    onChange={handleChangePersonalInfo}
                    className="mt-1 w-full p-3 border text-sm border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-200"
                    rows="4"
                    placeholder="Describe desired changes"
                    aria-label="Things to change"
                  />
                  {personalInformationErrors.thingsToChange && (
                    <p className="mt-1 text-sm text-red-600">
                      {personalInformationErrors.thingsToChange}
                    </p>
                  )}
                </div> */}
              </div>
            ) : (
              <div className="space-y-4">
                {/* <div className=" bg-white border border-gray-200 rounded-lg p-4">
                  <strong className="text-sm">Things Liked:</strong>
                  <div className="mt-2">
                    <p className="text-gray-700 mt-1 whitespace-pre-line">
                      {personalInformation.thingsLiked.value}
                    </p>
                  </div>
                </div>

                <div className=" bg-white border border-gray-200 rounded-lg p-4">
                  <strong className="text-sm">
                    Main assets and good points:
                  </strong>
                  <div className="mt-2">
                    {renderListItems(personalInformation.assets.value)}
                  </div>
                </div>
                <div className=" bg-white border border-gray-200 rounded-lg p-4">
                  <strong className="text-sm">Main bad points:</strong>
                  <div className="mt-2">
                    {renderListItems(personalInformation.badPoints.value)}
                  </div>
                </div> */}
                <div className=" bg-white border border-gray-200 rounded-lg p-4">
                  <strong className="text-sm">Main Social Difficulties:</strong>
                  <div className="mt-2">
                    <p className="text-gray-700 mt-1 whitespace-pre-line">
                      {personalInformation.socialDifficulties.value}
                    </p>
                  </div>
                </div>
                <div className=" bg-white border border-gray-200 rounded-lg p-4">
                  <strong className="text-sm">
                    Main Love/Sex Difficulties:
                  </strong>
                  <div className="mt-2">
                    <p className="text-gray-700 mt-1 whitespace-pre-line">
                      {personalInformation.loveSexDifficulties.value}
                    </p>
                  </div>
                </div>
                <div className=" bg-white border border-gray-200 rounded-lg p-4">
                  <strong className="text-sm">
                    Main Work Difficulties:
                  </strong>
                  <div className="mt-2">
                    <p className="text-gray-700 mt-1 whitespace-pre-line">
                      {personalInformation.schoolWorkDifficulties.value ||
                         <p className="text-gray-500 italic">N/A</p>}
                    </p>
                  </div>
                </div>
                {/* <div className=" bg-white border border-gray-200 rounded-lg p-4">
                  <strong className="text-sm">Main Life Goals:</strong>
                  <div className="mt-2">
                    <p className="text-gray-700 mt-1 whitespace-pre-line">
                      {personalInformation.lifeGoals.value}
                    </p>
                  </div>
                </div>
                <div className=" bg-white border border-gray-200 rounded-lg p-4">
                  <strong className="text-sm">
                    Things Most Like to Change:
                  </strong>
                  <div className="mt-2">
                    <p className="text-gray-700 mt-1 whitespace-pre-line">
                      {personalInformation.thingsToChange.value}
                    </p>
                  </div>
                </div> */}
              </div>
            )}
          </section>
:
          <section className="mb-12">
      <PersonalInsightsPrint personalInformation={personalInformation} />
    </section>
}

          {/* Occupation Information */}
                        {!printPreviewMode ?
          <section className="mb-12">
            <div className="flex justify-between items-center mb-2  pb-2">
              <h3 className="text-xl font-semibold text-gray-700">
                Occupation Information
              </h3>
              {mode !== "add" && (
                <div className="flex space-x-4">
                  <EditButton
                    onClick={() => toggleSectionEdit("occupation")}
                    ariaLabel={
                      editingSection === "occupation"
                        ? "Save Occupation Info"
                        : "Edit Occupation Info"
                    }
                    disabled={isSaving}
                  >
                    {editingSection === "occupation"
                      ? isSaving
                        ? "Saving..."
                        : "Save"
                      : "Edit"}
                  </EditButton>

    
                  {editingSection === "occupation" && (
                    <button
                      onClick={() => handleCancel("occupation")}
                      className="flex items-center bg-gray-300 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-400 transition-all duration-200"
                      aria-label="Cancel Editing"
                      disabled={isSaving}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              )}
            </div>
            {editingSection === "occupation" || mode === "add" ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      What occupation(s) have you mainly been trained for?{" "}
                      {personalInformation.occupationTrained.required && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>





                    {/* <TypeableDropdown
                          options={occupations}
                          value={{ value: personalInformation.occupationTrained?.value,name:personalInformation.occupationTrained?.value}}
                          onChange={(option) => handleSubjectChange(option,'occupationTrained')}
                          placeholder="Select or type Occupation trained for"
                          //isDisabled={!qualification.isNew === true}
                          className="mt-1"
                          classNamePrefix="select"
                            aria-label="Occupation trained for"
                        /> */}



                    {/* Occupation Trained Dropdown */}
<div className="flex items-center justify-start gap-2">
  <select
    name="occupationTrained"
    value={personalInformation.occupationTrained.value}
    onChange={(e) => {
      const val = e.target.value;
      if (val === "0") {
        setShowOccuptionAddPanel(true);
        setEditingSection("occupation"); // Ensure panel stays open
          setSelectedOccupationField("occupationTrained");
      } else {
        handleChangePersonalInfo(e);
        setShowOccuptionAddPanel(false);
      }
    }}
    className="mt-1 w-full p-3 border text-sm border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-200"
    aria-label="Occupation trained for"
  >
    <option value="">Select occupation</option>
    <option value="0">+ Add New Occupation</option>
    {occupations.map((occupation) => (
      <option key={occupation.id} value={occupation.name}>
        {occupation.name}
      </option>
    ))}
  </select>

  <button
    title="Refresh dropdown"
    onClick={async () => {
      setIsOccupationLoading(true);
      await loadDrpOccupations();
      setIsOccupationLoading(false);
    }}
    disabled={isOccupationLoading}
    className="text-gray-600 hover:text-sky-600"
  >
    <FaSync />
  </button>
</div>

                    {personalInformationErrors.occupationTrained && (
                      <p className="mt-1 text-sm text-red-600">
                        {personalInformationErrors.occupationTrained}
                      </p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Present Occupation
                      {personalInformation.occupation.required && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    {/* <TypeableDropdown
                          options={occupations}
                          value={{ value: personalInformation.occupation?.value,name:personalInformation.occupation?.value}}
                            onChange={(option) => handleSubjectChange(option,'occupation')}
                          placeholder="Select or type occupation"
                          //isDisabled={!qualification.isNew === true}
                          className="mt-1"
                          classNamePrefix="select"
                          aria-label={`Present occupation}`}
                        /> */}
                    <div className="flex items-center justify-start gap-2">
                  
<select
    name="occupation"
    value={personalInformation.occupation.value}
    onChange={(e) => {
      const val = e.target.value;
      if (val === "0") {
        setShowOccuptionAddPanel(true);
        setEditingSection("occupation");
        setSelectedOccupationField("occupation");
      } else {
        handleChangePersonalInfo(e);
        setShowOccuptionAddPanel(false);
      }
    }}
    className="mt-1 w-full p-3 border text-sm border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-200"
    aria-label="Present occupation"
  >
    <option value="">Select occupation</option>
    <option value="0">+ Add New Occupation</option>
    {occupations.map((occupation) => (
      <option key={occupation.id} value={occupation.name}>
        {occupation.name}
      </option>
    ))}
  </select>

  <button
    title="Refresh dropdown"
    onClick={async () => {
      setIsOccupationLoading(true);
      await loadDrpOccupations();
      setIsOccupationLoading(false);
    }}
    disabled={isOccupationLoading}
    className="text-gray-600 hover:text-sky-600"
  >
    <FaSync />
  </button>

                   
                    </div>

                    {personalInformationErrors.occupation && (
                      <p className="mt-1 text-sm text-red-600">
                        {personalInformationErrors.occupation}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Occupation Status
                      {personalInformation.occupationFullTime.required && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <div className="flex items-center space-x-4 mt-5">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="occupationFullTime"
                          value="full-time"
                          checked={
                            personalInformation.occupationFullTime.value ===
                            "full-time"
                          }
                          onChange={handleOccupationStatusChange}
                          className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300"
                          aria-label="Full-time"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          Full-time
                        </span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="occupationFullTime"
                          value="part-time"
                          checked={
                            personalInformation.occupationFullTime.value ===
                            "part-time"
                          }
                          onChange={handleOccupationStatusChange}
                          className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300"
                          aria-label="Part-time"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          Part-time
                        </span>
                      </label>
                    </div>
                    {personalInformationErrors.occupationFullTime && (
                      <p className="mt-1 text-sm text-red-600">
                        {personalInformationErrors.occupationFullTime}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                <div className="border-2 bg-white  border-gray-200 rounded-lg p-4 ">
                  <div className="flex justify-between items-center py-2.5 border-b border-gray-200 last:border-b-0">
                    <span className="font-bold text-sm w-1/2">
                      What occupation(s) have you mainly been trained for?
                    </span>
                    <span className="text-gray-700 text-sm w-1/2 text-right">
                      {personalInformation.occupationTrained.value ? personalInformation.occupationTrained.value : <p className="text-gray-500 italic">N/A</p>}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2.5 border-b border-gray-200 last:border-b-0">
                    <span className="font-bold text-sm w-1/3">
                      Present Occupation
                    </span>
                    <span className="text-gray-700 text-sm w-2/3 text-right">
                      {personalInformation.occupation.value ? personalInformation.occupation.value :  <p className="text-gray-500 italic">N/A</p>}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2.5 border-b border-gray-200 last:border-b-0">
                    <span className="font-bold text-sm w-1/3">
                      Occupation Status
                    </span>
                    <span className="text-gray-700 text-sm w-2/3 text-right">
                      {personalInformation.occupationFullTime.value ? personalInformation.occupationFullTime.value:  <p className="text-gray-500 italic">N/A</p>}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </section>
:
          <section className="mb-12">
      <OccupationInfoPrint personalInformation={personalInformation} />
    </section>}
    

          {mode === "add" && (
            <div className="flex justify-end space-x-4 mt-6">
              <button
                type="button"
                onClick={handleSubmitPersonalInformation}
                className="flex items-center bg-sky-600 text-white px-6 py-3 rounded-lg hover:bg-sky-700 transition-all duration-200 shadow-md"
                aria-label="Save and go to next tab"
              >
                {isSaving ? "Saving..." : "Save"}
              </button>
            </div>
          )}
        </div>
      ) : (
        <LoadingSpinner />
      )}
    </>
  );
};

export default TabPersonalInformationIndividual;
